import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckOutlined } from '@ant-design/icons';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SCAN_DURATION_S = 10;
const MIN_SCAN_DURATION_MS = 10000;

// Placeholder annotations shown while waiting for real insights
const PLACEHOLDER_ANNOTATIONS = [
  { label: 'Analyzing page structure…', delay: 1.5 },
  { label: 'Scanning visual elements…', delay: 3.5 },
  { label: 'Reading page content…', delay: 5.5 },
  { label: 'Identifying key sections…', delay: 7.5 },
  { label: 'Extracting business signals…', delay: 9.0 },
];

const TOTAL_SLOTS = 8;

const INSIGHT_MESSAGES = [
  'Extracting brand identity…',
  'Mapping conversion funnels…',
  'Analyzing content strategy…',
  'Identifying target audience signals…',
  'Evaluating competitive positioning…',
  "This may take a minute…",
];

// ---------------------------------------------------------------------------
// ScanOverlay — website scan animation with screenshot/logo/placeholder
// ---------------------------------------------------------------------------

export interface ScanInsight {
  label: string;
  category: string;
}

interface ScanOverlayProps {
  companyUrl: string;
  companyName: string;
  screenshotUrl: string | null;
  logoUrl: string | null;
  insights: ScanInsight[];
  onMinDurationReached: () => void;
}

export default function ScanOverlay({
  companyUrl,
  companyName,
  screenshotUrl,
  logoUrl,
  insights,
  onMinDurationReached,
}: ScanOverlayProps) {
  const [insightIdx, setInsightIdx] = useState(0);
  const [logoFailed, setLogoFailed] = useState(false);
  const minDurationFired = useRef(false);

  // Unified display list — only grows. Real insights append as they arrive;
  // placeholder items drip in as a fallback when the server is slow.
  const [displayedLabels, setDisplayedLabels] = useState<string[]>([]);
  const realArrivedRef = useRef(false);
  const placeholderIdxRef = useRef(0);

  // Fire minimum-duration callback after MIN_SCAN_DURATION_MS
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!minDurationFired.current) {
        minDurationFired.current = true;
        onMinDurationReached();
      }
    }, MIN_SCAN_DURATION_MS);
    return () => clearTimeout(timer);
  }, [onMinDurationReached]);

  // Cycle insight messages
  useEffect(() => {
    const id = setInterval(() => {
      setInsightIdx((prev) => (prev + 1) % INSIGHT_MESSAGES.length);
    }, 2000);
    return () => clearInterval(id);
  }, []);

  // Queue real insights and drip them in one at a time
  const realQueueRef = useRef<string[]>([]);
  const drainTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const REAL_STAGGER_MS = 3000;

  useEffect(() => {
    if (insights.length === 0) return;
    realArrivedRef.current = true;

    // Add any new labels to the queue (deduped against already-displayed + already-queued)
    setDisplayedLabels((prev) => {
      const seen = new Set([...prev, ...realQueueRef.current]);
      const fresh = insights
        .slice(0, TOTAL_SLOTS)
        .map((ins) => ins.label)
        .filter((l) => !seen.has(l));
      realQueueRef.current.push(...fresh);
      return prev; // don't mutate yet — the interval drains the queue
    });

    // Start draining if not already running
    if (!drainTimerRef.current) {
      // Append the first one immediately so the user sees something right away
      setDisplayedLabels((prev) => {
        const next = realQueueRef.current.shift();
        if (!next || prev.length >= TOTAL_SLOTS) return prev;
        return [...prev, next];
      });

      drainTimerRef.current = setInterval(() => {
        const next = realQueueRef.current.shift();
        if (!next) { clearInterval(drainTimerRef.current!); drainTimerRef.current = null; return; }
        setDisplayedLabels((prev) => {
          if (prev.length >= TOTAL_SLOTS) return prev;
          return [...prev, next];
        });
      }, REAL_STAGGER_MS);
    }
  }, [insights]);

  // Cleanup drain interval on unmount
  useEffect(() => {
    return () => { if (drainTimerRef.current) clearInterval(drainTimerRef.current); };
  }, []);

  // Fallback: if no real insights after PLACEHOLDER_DELAY_MS, drip-feed
  // placeholder items every PLACEHOLDER_STAGGER_MS until real ones arrive.
  useEffect(() => {
    const PLACEHOLDER_DELAY_MS = 2000;
    const PLACEHOLDER_STAGGER_MS = 3000;

    const delayTimer = setTimeout(() => {
      const interval = setInterval(() => {
        if (realArrivedRef.current) { clearInterval(interval); return; }
        if (placeholderIdxRef.current >= PLACEHOLDER_ANNOTATIONS.length) { clearInterval(interval); return; }
        const label = PLACEHOLDER_ANNOTATIONS[placeholderIdxRef.current].label;
        placeholderIdxRef.current += 1;
        setDisplayedLabels((prev) => {
          if (prev.length >= TOTAL_SLOTS) return prev;
          return [...prev, label];
        });
      }, PLACEHOLDER_STAGGER_MS);
      return () => clearInterval(interval);
    }, PLACEHOLDER_DELAY_MS);

    return () => clearTimeout(delayTimer);
  }, []);

  // Pad to TOTAL_SLOTS so the grid never changes size
  const annotations = Array.from({ length: TOTAL_SLOTS }, (_, i) =>
    i < displayedLabels.length
      ? { label: displayedLabels[i], visible: true }
      : { label: '\u00A0', visible: false },
  );

  // Priority: screenshot > logo > company name placeholder
  const showScreenshot = !!screenshotUrl;
  const showLogo = !showScreenshot && !!logoUrl && !logoFailed;
  const showFallback = !showScreenshot && !showLogo;

  // Display name for the placeholder — prefer clean URL domain over raw workspace name
  const displayName = companyUrl
    ? companyUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, '')
    : companyName;

  return (
    <div className="flex flex-col items-center w-full">
        {/* Browser chrome + website preview */}
        <div className="relative w-3/4 mx-auto pt-10">
          <div
            className="relative w-full overflow-hidden rounded-lg"
            style={{ border: '1.5px solid var(--color-light400)' }}
          >
            {/* Browser tab bar */}
            <div
              className="relative flex items-center px-2 h-3.5"
              style={{ background: 'var(--color-light400)' }}
            >
              {/* Traffic light dots */}
              <div className="flex gap-[3px]">
                <div className="w-[5px] h-[5px] rounded-full bg-[#ff5f57]" />
                <div className="w-[5px] h-[5px] rounded-full bg-[#febc2e]" />
                <div className="w-[5px] h-[5px] rounded-full bg-[#28c840]" />
              </div>
            </div>

            {/* Website content area — aspect ratio matches a laptop screen */}
            <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16 / 10' }}>
              {/* Screenshot — best visual, from Jina via backend */}
              {showScreenshot && (
                <div className="absolute inset-0 overflow-hidden">
                  <img
                    src={screenshotUrl}
                    alt="Your website"
                    className="w-full object-cover object-top"
                    style={{
                      animation: `scanScroll ${SCAN_DURATION_S}s ease-in-out forwards`,
                    }}
                  />
                </div>
              )}

              {/* Logo fallback — company logo centered in scan area */}
              {showLogo && (
                <div className="absolute inset-0 flex items-center justify-center bg-white">
                  <img
                    src={logoUrl}
                    alt="Company logo"
                    className="max-w-[40%] max-h-[40%] object-contain"
                    style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))' }}
                    onError={() => setLogoFailed(true)}
                  />
                </div>
              )}

              {/* Fallback — white screen with company domain */}
              {showFallback && (
                <div className="absolute inset-0 flex items-center justify-center bg-white">
                  <p
                    className="text-2xl font-semibold tracking-tight"
                    style={{ color: 'var(--color-black)' }}
                  >
                    {displayName}
                  </p>
                </div>
              )}

              {/* Scan overlay */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Translucent gradient */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(180deg, rgba(59, 130, 246, 0.03) 0%, transparent 50%, rgba(59, 130, 246, 0.03) 100%)',
                  }}
                />

                {/* Scan line */}
                <div
                  className="absolute left-0 right-0 h-[2px]"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.6), transparent)',
                    boxShadow: '0 0 12px rgba(59, 130, 246, 0.3), 0 0 24px rgba(59, 130, 246, 0.15)',
                    animation: `scanLine ${SCAN_DURATION_S}s linear forwards`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Shimmer text + chips below the browser frame */}
        <div className="flex flex-col items-center gap-4 py-10 pb-10 w-full">
          <AnimatePresence mode="wait">
            <motion.p
              key={insightIdx}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.3 }}
              className="text-sm font-medium text-center font-manrope"
              style={{
                background: 'linear-gradient(90deg, var(--color-body-text) 25%, var(--color-content-light) 50%, var(--color-body-text) 75%)',
                backgroundSize: '200% 100%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'shimmer 2s linear infinite',
              }}
            >
              {INSIGHT_MESSAGES[insightIdx]}
            </motion.p>
          </AnimatePresence>

          {/* Annotation list — all slots pre-rendered, content fades in row-by-row */}
          <div
            className="mt-2"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px 40px',
              maxWidth: 640,
              margin: '0 auto',
              width: '100%',
            }}
          >
            {annotations.map((a, i) => (
              <div
                key={`slot-${i}`}
                className="flex items-center gap-2.5 text-sm whitespace-nowrap font-manrope"
                style={{
                  color: 'var(--color-leo-secondary)',
                  height: 20,
                  opacity: a.visible ? 1 : 0,
                  transition: 'opacity 0.3s ease-out',
                }}
              >
                <CheckOutlined style={{ fontSize: 10, color: 'var(--color-leo-secondary)', display: 'flex', alignItems: 'center' }} />
                <span>{a.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CSS animations */}
        <style>{`
          @keyframes scanLine {
            0% { top: 0%; }
            100% { top: 100%; }
          }
          @keyframes scanScroll {
            0% { transform: translateY(0); }
            100% { transform: translateY(-30%); }
          }
          @keyframes shimmer {
            0% { background-position: 200% center; }
            100% { background-position: -200% center; }
          }
        `}</style>
    </div>
  );
}
